const EPSILON = 0.00001; // ~1 meter

const toNum = v =>
  v === null || v === undefined || v === '' ? null : Number(v);

//likely not a meaningful change if lat/lng changed by anything less than 1 meter
const nearlyEqual = (a, b, epsilon = EPSILON) =>
  Math.abs(a - b) < epsilon;

const coordsChanged = (a, b) => {
  const n1 = toNum(a);
  const n2 = toNum(b);

  if (n1 === null || n2 === null) return n1 !== n2;
  return !nearlyEqual(n1, n2);
};

/**
 * Returns true if ANY updated AGM has a meaningful lat/lng change that should trigger a subchain warning.
 * If true, warn user that they should check their subchains as distance to US/DS sites may have changed.
 * 
 * Meaningful means, apart from differences of 0.00001:
 * - Est_Lat or Est_Lng has changed
 * - Real_Lat or Real_Lng has changed (except from going from not filled in -> filled in - in that case, compare to Est Lat/Lng)
 * 
 * @param {Object[]} originalData
 * @param {Object[]} updatedAGMs
 * 
 * @returns {boolean}
 */
export const hasAGMLatLngChange = ({
  originalData,
  updatedAGMs
}) => {
  if (!originalData?.length || !updatedAGMs?.length) return false;

  const originalById = new Map(
    originalData.map(agm => [agm.AGMID, agm])
  );

  return updatedAGMs.some(updated => {
    const original = originalById.get(updated.AGMID);
    if (!original) return false;

    // Est lat/lng: always warn about changes (since est lat/lng is required and will never not be filled in)
    if (
      coordsChanged(original.Est_Lat, updated.Est_Lat) ||
      coordsChanged(original.Est_Lng, updated.Est_Lng)
    ) {
      return true;
    }

    // Real lat/lng: don't warn if going from 0 (not filled in) to a value (filled in), unless the new filled in value is different than est lat/lng 
    const origRealLat = toNum(original.Real_Lat);
    const newRealLat  = toNum(updated.Real_Lat);
    const origRealLng = toNum(original.Real_Lng);
    const newRealLng  = toNum(updated.Real_Lng);

    const estLat = toNum(updated.Est_Lat);
    const estLng = toNum(updated.Est_Lng);

    const realLatChanged =
      coordsChanged(origRealLat, newRealLat) &&
      !(origRealLat === 0 && newRealLat !== null && nearlyEqual(newRealLat, estLat));

    const realLngChanged =
      coordsChanged(origRealLng, newRealLng) &&
      !(origRealLng === 0 && newRealLng !== null && nearlyEqual(newRealLng, estLng));

    return realLatChanged || realLngChanged;
  });
};
